import React, { useState, useEffect, useMemo } from 'react';
import {
    ShieldCheck,
    Mail,
    Clock,
    User,
    Globe,
    SearchIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';
import { getPlanDetails } from '../../utils/planUtils';
import { formatDateToPST } from '../../utils/dateUtils';

const LoginLogsTable = () => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);

    const filteredLogs = useMemo(() => {
        return logs.filter(log =>
            (log.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.email || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [logs, searchTerm]);

    useEffect(() => {
        const fetchLogs = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('accessToken');
                const response = await fetch(`${API_BASE}/admin/login-logs`, {
                    headers: {
                        'Authorization': `Bearer ` + token
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setLogs(data);
                }
            } catch (error) {
                console.error("Error fetching login logs:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchLogs();
    }, []);


    return (
        <div className="flex flex-col space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                <div className="flex flex-col gap-2 pl-4">
                    <div className="flex items-center space-x-3 mb-1">
                        <div className="w-10 h-1 bg-indigo-600 rounded-full"></div>
                        <h1 className="text-5xl font-black text-slate-900 tracking-tighter leading-none flex items-center gap-4">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500">Authentication <span className="text-indigo-600">History</span></span>
                            <div className="h-2 w-2 rounded-full bg-indigo-500 mt-2 animate-pulse" />
                        </h1>
                    </div>
                    <p className="text-slate-500 font-bold text-sm mt-1 max-w-xl leading-relaxed">
                        Real-time access telemetry across all access points
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative group">
                        <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search access logs..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-12 pr-6 py-3 bg-white rounded-2xl text-xs font-bold border border-slate-200/60 focus:border-indigo-600/30 w-72 transition-all outline-none shadow-sm focus:shadow-md"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-2xl shadow-slate-200/40 overflow-hidden">
                <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-900 border-b border-white/10">
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Member Profile</th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Subscription Status</th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Login Timestamp</th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]" style={{ minWidth: '150px' }}>Method</th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em] text-right">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/50">
                            {isLoading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-10 py-8">
                                            <div className="h-12 bg-slate-50 rounded-2xl w-full"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : filteredLogs.length > 0 ? (
                                filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((log, idx) => (
                                    <motion.tr
                                        key={log.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.03 }}
                                        className="group transition-all hover:bg-slate-50/50"
                                    >
                                        <td className="px-10 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-slate-900/5 flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white transition-all duration-300 overflow-hidden relative">
                                                    {log.profile_pic ? (
                                                        <img src={log.profile_pic} alt={log.full_name || 'User'} className="w-full h-full object-cover relative z-10" referrerPolicy="no-referrer" />
                                                    ) : log.full_name || log.email ? (
                                                        <span className="font-black text-sm relative z-10">{(log.full_name || log.email).charAt(0).toUpperCase()}</span>
                                                    ) : (
                                                        <User className="w-4 h-4 relative z-10" />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{log.full_name || 'Anonymous'}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Mail className="w-3 h-3 text-slate-300" />
                                                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.05em]">{log.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-6">
                                            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${getPlanDetails(log.plan_name, log.subscription_status).bg}`}>
                                                <div className={`w-1 h-1 rounded-full ${getPlanDetails(log.plan_name, log.subscription_status).dot} ${log.subscription_status === 'active' ? 'animate-pulse' : ''}`} />
                                                {getPlanDetails(log.plan_name, log.subscription_status).label}
                                            </div>
                                        </td>
                                        <td className="px-10 py-6">
                                            <div className="flex items-center gap-3">
                                                <Clock className="w-4 h-4 text-slate-300" />
                                                <span className="text-[10px] font-mono font-black text-slate-700 uppercase">{formatDateToPST(log.login_time)}</span>
                                            </div>
                                        </td>
                                        <td className="px-10 py-6">
                                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border ${log.login_method === 'google'
                                                ? 'bg-blue-50 text-blue-600 border-blue-100'
                                                : 'bg-indigo-50 text-indigo-600 border-indigo-100'
                                                }`}>
                                                {log.login_method === 'google' ? <Globe className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                                                {log.login_method}
                                            </div>
                                        </td>
                                        <td className="px-10 py-6 text-right">
                                            <div className="inline-flex items-center gap-2 px-2 py-1 bg-emerald-50 rounded-lg text-emerald-600">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                                <span className="text-[9px] font-black uppercase tracking-widest">Verified</span>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-10 py-20 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                                                <SearchIcon className="w-6 h-6 text-slate-200" />
                                            </div>
                                            <p className="text-[11px] font-black text-slate-300 uppercase tracking-[0.4em]">Zero Encounters in Current Sector</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* High-Standard Pagination Controller */}
                {filteredLogs.length > itemsPerPage && (
                    <div className="px-10 py-6 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <div className="flex items-center gap-6">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                Showing <span className="text-slate-900">{Math.min(filteredLogs.length, (currentPage - 1) * itemsPerPage + 1)}-{Math.min(filteredLogs.length, currentPage * itemsPerPage)}</span> of <span className="text-slate-900">{filteredLogs.length}</span> results
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:shadow-md disabled:opacity-30 disabled:hover:shadow-none transition-all"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>

                            <div className="flex items-center px-2 gap-1.5">
                                {[...Array(Math.ceil(filteredLogs.length / itemsPerPage))].map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentPage(i + 1)}
                                        className={`w-8 h-8 rounded-xl text-[10px] font-black transition-all ${currentPage === i + 1
                                            ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
                                            : 'text-slate-400 hover:bg-white hover:text-slate-900'
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredLogs.length / itemsPerPage), prev + 1))}
                                disabled={currentPage === Math.ceil(filteredLogs.length / itemsPerPage)}
                                className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:shadow-md disabled:opacity-30 disabled:hover:shadow-none transition-all"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginLogsTable;
